import './applicantIncomeDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const {
    additionalIncomeAmount,
    additionalIncomeDurationMonths,
    additionalIncomeDurationYears,
    additionalIncomeEmployerName,
    additionalIncomeFrequency,
    additionalIncomeJobTitle,
    additionalIncomeType,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setAdditionalIncomeAmount,
    setAdditionalIncomeDurationMonths,
    setAdditionalIncomeDurationYears,
    setAdditionalIncomeEmployerName,
    setAdditionalIncomeFrequency,
    setAdditionalIncomeJobTitle,
    setAdditionalIncomeType,
    syncIncome,
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      incomeAmountValue: toCurrencyString(additionalIncomeAmount),
      incomeDurationMonthsValue: additionalIncomeDurationMonths,
      incomeDurationYearsValue: additionalIncomeDurationYears,
      incomeEmployerNameValue: additionalIncomeEmployerName,
      incomeFrequencyValue: additionalIncomeFrequency,
      incomeJobTitleValue: additionalIncomeJobTitle,
      incomeTypeValue: additionalIncomeType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      incomeTypeValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeAmountValue,
    } = values;

    if (!incomeTypeValue || !incomeFrequencyValue || !incomeAmountValue) {
      return;
    }

    setAdditionalIncomeAmount(incomeAmountValue);
    setAdditionalIncomeFrequency(incomeFrequencyValue);
    setAdditionalIncomeType(incomeTypeValue);

    if (showSecondaryEmploymentFields) {
      setAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
      setAdditionalIncomeDurationYears(incomeDurationYearsValue);
      setAdditionalIncomeEmployerName(incomeEmployerNameValue);
      setAdditionalIncomeJobTitle(incomeJobTitleValue);
    } else {
      setAdditionalIncomeDurationMonths(null);
      setAdditionalIncomeDurationYears(null);
      setAdditionalIncomeEmployerName(null);
      setAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-income-details-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantIncomeDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantIncomeDetails.form.h1}>
                {t('ApplicantIncomeDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantIncomeDetails.form.h2}>
                {t('ApplicantIncomeDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantIncomeDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('incomeTypeValue', incomeTypeValidation)}
                    error={errors.incomeTypeValue}
                    errorTestId={
                      locator.pages.applicantIncomeDetails.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantIncomeDetails.form.incomeTypeSelect
                    }
                    label={t('ApplicantIncomeDetailsPage.incomeType')}
                    options={incomeTypeOptions}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      data-testid={
                        locator.pages.applicantIncomeDetails.form
                          .employerNameInput
                      }
                      error={errors.incomeEmployerNameValue}
                      errorTestId={
                        locator.pages.applicantIncomeDetails.form
                          .employerNameInputError
                      }
                      label={t('ApplicantIncomeDetailsPage.employerName')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      data-testid={
                        locator.pages.applicantIncomeDetails.form.jobTitleInput
                      }
                      error={errors.incomeJobTitleValue}
                      errorTestId={
                        locator.pages.applicantIncomeDetails.form
                          .jobTitleInputError
                      }
                      label={t('ApplicantIncomeDetailsPage.jobTitle')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        {...register(
                          'incomeDurationYearsValue',
                          incomeDurationYearsValidation,
                        )}
                        error={errors.incomeDurationYearsValue}
                        errorTestId={
                          locator.pages.applicantIncomeDetails.form
                            .yearSelectError
                        }
                        inputTestId={
                          locator.pages.applicantIncomeDetails.form.yearSelect
                        }
                        label={t('ApplicantIncomeDetailsPage.durationYears')}
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        {...register(
                          'incomeDurationMonthsValue',
                          incomeDurationMonthsValidation,
                        )}
                        error={errors.incomeDurationMonthsValue}
                        errorTestId={
                          locator.pages.applicantIncomeDetails.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.applicantIncomeDetails.form.monthSelect
                        }
                        label={t('ApplicantIncomeDetailsPage.durationMonths')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register(
                      'incomeFrequencyValue',
                      incomeFrequencyValidation,
                    )}
                    error={errors.incomeFrequencyValue}
                    errorTestId={
                      locator.pages.applicantIncomeDetails.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.applicantIncomeDetails.form.frequencySelect
                    }
                    label={t('ApplicantIncomeDetailsPage.incomeFrequency')}
                    options={incomeFrequencyOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    data-testid={
                      locator.pages.applicantIncomeDetails.form.amountInput
                    }
                    error={errors.incomeAmountValue}
                    errorTestId={
                      locator.pages.applicantIncomeDetails.form.amountInputError
                    }
                    label={t('ApplicantIncomeDetailsPage.incomeAmount')}
                    currency
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantIncomeDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingIncome}
                  fullWidth
                  submit
                >
                  {t('ApplicantIncomeDetailsPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={locator.pages.applicantIncomeDetails.form.alimonyDisclosure}
              >
                {t('disclosures.alimony')}
              </p>
            </div>
            <footer
              data-testid={locator.pages.applicantIncomeDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantIncomeDetailsPage';
